import React from 'react'
import clsx from 'clsx'
import { KeyValueText } from 'components'
import { defaultFallbackString, Typography } from 'cs-kevlar-react'
import { createStyles, makeStyles, Theme } from 'cs-kevlar-react'
import { SL90, EL90 } from 'cs-kevlar-react/lib/es/theming/palette'
import { ClassNameMap } from '@mui/styles'
import compare_func from 'compare-func'

type ClassNames = 'keyValueText' | 'value' | 'diffAdded' | 'diffRemoved'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    keyValueText: {
      paddingTop: theme.spacing(8),
    },
    value: {
      marginBottom: theme.spacing(1),
      width: 'fit-content',
      wordBreak: 'break-all',
    },
    diffAdded: {
      background: SL90,
      padding: theme.spacing(0, 1),
    },
    diffRemoved: {
      background: EL90,
      textDecoration: 'line-through',
      padding: theme.spacing(0, 1),
    },
  }),
)

const isEmpty = (value: string | null | undefined) =>
  !value || value.length === 0 || !value.trim()

const renderUndiffedContent = (
  updated: string[] | null | undefined,
  classes: ClassNameMap<ClassNames>,
) => {
  var nonEmptyValues = updated?.filter((x) => !isEmpty(x)) ?? []
  if (nonEmptyValues.length === 0)
    return (
      <Typography className={classes.value}>{defaultFallbackString}</Typography>
    )

  return nonEmptyValues.map((x) => (
    <Typography key={x} className={classes.value}>
      {x}
    </Typography>
  ))
}

const renderDiffedContent = (
  original: string[] | null | undefined,
  updated: string[] | null | undefined,
  classes: ClassNameMap<ClassNames>,
) => {
  var nonEmptyOriginalValues = new Set(
    original?.filter((x) => !isEmpty(x)) ?? [],
  )
  var nonEmptyUpdatedValues = new Set(updated?.filter((x) => !isEmpty(x)) ?? [])

  var constantValues = [...nonEmptyOriginalValues]
    .filter((x) => nonEmptyUpdatedValues.has(x))
    .map((x) => [x, ''])
  var addedValues = [...nonEmptyUpdatedValues]
    .filter((x) => !nonEmptyOriginalValues.has(x))
    .map((x) => [x, classes.diffAdded])
  var removedValues = [...nonEmptyOriginalValues]
    .filter((x) => !nonEmptyUpdatedValues.has(x))
    .map((x) => [x, classes.diffRemoved])

  var allValues = [...constantValues, ...removedValues, ...addedValues].sort(
    compare_func((x) => x[0]),
  )

  if (allValues.length === 0)
    return (
      <Typography className={classes.value}>{defaultFallbackString}</Typography>
    )

  return allValues.map((x) => (
    <Typography key={x[0]} className={clsx(classes.value, x[1])}>
      {x[0]}
    </Typography>
  ))
}

interface DetailArrayFieldProps {
  label: string
  original: string[] | null | undefined
  updated: string[] | null | undefined
  showDiff?: boolean
}

export const DetailArrayField: React.FunctionComponent<DetailArrayFieldProps> = ({
  label,
  original,
  updated,
  showDiff = false,
}) => {
  const classes = useStyles({})

  return (
    <React.Fragment>
      {!showDiff && (
        <KeyValueText label={label} className={classes.keyValueText}>
          {() => renderUndiffedContent(updated, classes)}
        </KeyValueText>
      )}

      {showDiff && (
        // By giving the KeyValueText a function instead of a direct child,
        // it won't wrap the content in a Typography component, but use the content directly.
        <KeyValueText label={label} className={classes.keyValueText}>
          {() => renderDiffedContent(original, updated, classes)}
        </KeyValueText>
      )}
    </React.Fragment>
  )
}
